/**
 * Phase 1 Chat Refactoring: Message Management Reducer
 * 
 * This hook replaces the complex useMessageState with a cleaner reducer pattern.
 * Key improvements:
 * - Predictable state updates via actions
 * - Immutable state management
 * - Separated concerns from chat logic
 * - Performance optimized with stable operations
 * - Proper TypeScript discriminated unions
 * 
 * @see docs/chat-refactoring-plan.md Phase 1
 */

import { useReducer, useCallback, useMemo } from 'react';
import { ChatMessage } from '../entities';

/**
 * Discriminated union of all possible message actions
 * Each action is fully typed with required payload
 */
type MessageAction = 
  | { type: 'ADD_USER_MESSAGE'; message: ChatMessage }
  | { type: 'ADD_ASSISTANT_MESSAGE'; message: ChatMessage }
  | { type: 'CREATE_ASSISTANT_PLACEHOLDER'; messageId: string; sessionId: string; metadata?: any }
  | { type: 'UPDATE_ASSISTANT_MESSAGE'; messageId: string; chunk: string; isComplete: boolean; fullContent?: any; metadata?: any }
  | { type: 'REPLACE_MESSAGES'; messages: ChatMessage[] }
  | { type: 'REMOVE_MESSAGE'; messageId: string }
  | { type: 'UPDATE_USER_MESSAGE_ID'; tempId: string; newId: string }
  | { type: 'MARK_MESSAGE_ERROR'; messageId: string; error?: string }
  | { type: 'CLEAR_MESSAGES' };

/**
 * Pure reducer function for message state management
 * All updates are immutable and predictable
 */
const messageReducer = (state: ChatMessage[], action: MessageAction): ChatMessage[] => {
  switch (action.type) {
    case 'ADD_USER_MESSAGE':
      // Prevent duplicates by filtering both id and tempId matches
      // This handles optimistic updates and ensures clean state
      const filteredForUser = state.filter(m => m.id !== action.message.id && m.tempId !== action.message.tempId);
      return [...filteredForUser, action.message];
      
    case 'ADD_ASSISTANT_MESSAGE':
      // Prevent duplicates by filtering id matches
      // This adds complete assistant messages (like welcome messages)
      const filteredForAssistant = state.filter(m => m.id !== action.message.id);
      return [...filteredForAssistant, action.message];
      
    case 'CREATE_ASSISTANT_PLACEHOLDER':
      // Only create placeholder if it doesn't already exist
      // Prevents duplicate placeholders from multiple SignalR events
      const existing = state.find(m => m.id === action.messageId && m.role === 'assistant');
      if (existing) return state;
      
      const placeholder = ChatMessage.createAssistantPlaceholder(action.messageId, action.sessionId, action.metadata);
      return [...state, placeholder];
      
    case 'UPDATE_ASSISTANT_MESSAGE':
      // Incremental content updates for streaming messages
      // Properly handles streaming state and completion with metadata
      return state.map(msg => {
        if (msg.id === action.messageId && msg.role === 'assistant') {
          let newContent: string;
          
          if (action.isComplete && action.fullContent) {
            // For completion events, extract content from fullContent structure
            if (typeof action.fullContent === 'string') {
              newContent = action.fullContent;
            } else if (Array.isArray(action.fullContent)) {
              // Handle array format: use first element's text.value or fallback
              const firstContent = action.fullContent[0];
              newContent = firstContent?.text?.value || firstContent?.content || msg.content;
            } else if (action.fullContent?.text?.value) {
              // Handle object format: use text.value
              newContent = action.fullContent.text.value;
            } else {
              // Fallback to existing content
              newContent = msg.content + action.chunk;
            }
          } else {
            // Regular streaming: append chunk
            newContent = msg.content + action.chunk;
          }
            
          return new ChatMessage({
            ...msg,
            content: newContent,
            isStreaming: !action.isComplete,
            isPlaceholder: false,
            status: action.isComplete ? 'sent' : 'sending',
            timestamp: action.isComplete ? new Date().toISOString() : msg.timestamp,
            fullContent: action.isComplete && action.fullContent ? action.fullContent : msg.fullContent,
            metadata: action.metadata ? { ...msg.metadata, ...action.metadata } : msg.metadata
          });
        }
        return msg;
      });
      
    case 'REPLACE_MESSAGES':
      // Bulk replace for session switching or initial load
      return action.messages;
      
    case 'REMOVE_MESSAGE':
      // Clean removal by ID
      return state.filter(m => m.id !== action.messageId);
      
    case 'UPDATE_USER_MESSAGE_ID':
      // Handle optimistic update ID reconciliation
      // Updates both id and tempId to maintain consistency
      return state.map(msg => {
        if (msg.id === action.tempId || msg.tempId === action.tempId) {
          return new ChatMessage({
            ...msg,
            id: action.newId,
            tempId: action.tempId,
            status: 'sent'
          });
        }
        return msg;
      });
      
    case 'MARK_MESSAGE_ERROR':
      // Error state handling with metadata update
      return state.map(msg => {
        if (msg.id === action.messageId) {
          return new ChatMessage({
            ...msg,
            status: 'error',
            metadata: {
              ...msg.metadata,
              processingTime: -1 // Indicates error state
            }
          });
        }
        return msg;
      });
      
    case 'CLEAR_MESSAGES':
      // Complete state reset
      return [];
      
    default:
      // TypeScript ensures this is never reached
      return state;
  }
};

/**
 * Main hook for message state management using reducer pattern
 * 
 * Performance optimizations implemented:
 * - Stable operations object via useMemo (prevents dependency loops)
 * - Separated findMessage to avoid recreation issues
 * - No direct message dependencies in operations (prevents infinite renders)
 * 
 * @returns Stable interface compatible with existing useMessageState
 */
export const useMessageReducer = () => {
  const [messages, dispatch] = useReducer(messageReducer, []);
  
  /**
   * Stable operations object that doesn't depend on messages state
   * This prevents dependency loops in useEffect hooks
   * Each operation is a simple dispatch wrapper
   */
  const operations = useMemo(() => ({
    addUserMessage: (content: string, tempId?: string, sessionId?: string) => {
      if (!sessionId) throw new Error('Session ID required');
      const message = ChatMessage.createUserMessageWithTempId(content, sessionId, tempId);
      dispatch({ type: 'ADD_USER_MESSAGE', message });
      return message;
    },
    
    addAssistantMessage: (content: string, sessionId: string) => {
      if (!sessionId) throw new Error('Session ID required');
      const message = ChatMessage.createAssistantMessage(content, sessionId);
      dispatch({ type: 'ADD_ASSISTANT_MESSAGE', message });
      return message;
    },
    
    createAssistantPlaceholder: (messageId: string, sessionId: string, metadata?: any) => {
      dispatch({ type: 'CREATE_ASSISTANT_PLACEHOLDER', messageId, sessionId, metadata });
    },
    
    updateAssistantMessage: (messageId: string, chunk: string, isComplete: boolean, fullContent?: any, metadata?: any) => {
      dispatch({ type: 'UPDATE_ASSISTANT_MESSAGE', messageId, chunk, isComplete, fullContent, metadata });
    },
    
    replaceMessages: (newMessages: ChatMessage[]) => {
      dispatch({ type: 'REPLACE_MESSAGES', messages: newMessages });
    },
    
    removeMessage: (messageId: string) => {
      dispatch({ type: 'REMOVE_MESSAGE', messageId });
    },
    
    updateUserMessageId: (tempId: string, newId: string) => {
      dispatch({ type: 'UPDATE_USER_MESSAGE_ID', tempId, newId });
    },
    
    markMessageAsError: (messageId: string, error?: string) => {
      dispatch({ type: 'MARK_MESSAGE_ERROR', messageId, error });
    },
    
    clearMessages: () => {
      dispatch({ type: 'CLEAR_MESSAGES' });
    }
  }), []); // Empty dependency array - operations are always stable
  
  /**
   * Separate findMessage function that can use current messages
   * Isolated from operations to prevent the operations object from being recreated
   * when messages change, which would cause infinite dependency loops
   */
  const findMessage = useCallback((messageId: string, role?: 'user' | 'assistant') => {
    return messages.find(m => {
      const idMatch = m.id === messageId;
      const roleMatch = !role || m.role === role;
      return idMatch && roleMatch;
    });
  }, [messages]);
  
  return {
    messages,
    operations, // Stable reference
    findMessage, // Separate from operations to avoid recreation
    isStreamingAnyMessage: messages.some(m => m.isStreaming),
    
    // 🆕 NEW: Multi-message run utilities
    getMessagesByRunId: (runId: string) => {
      return messages.filter(m => m.metadata?.runId === runId);
    },
    
    isRunComplete: (runId: string) => {
      const runMessages = messages.filter(m => m.metadata?.runId === runId);
      if (runMessages.length === 0) return false;
      
      const lastMessage = runMessages.find(m => m.metadata?.isLastMessage);
      return lastMessage && !lastMessage.isStreaming;
    },
    
    getRunProgress: (runId: string) => {
      const runMessages = messages.filter(m => m.metadata?.runId === runId);
      if (runMessages.length === 0) return { current: 0, total: 0 };
      
      const totalMessages = runMessages[0]?.metadata?.totalMessages;
      const completedMessages = runMessages.filter(m => !m.isStreaming).length;
      
      return {
        current: completedMessages,
        total: totalMessages || runMessages.length
      };
    }
  };
};
